import React, { useState } from "react";
import { PlaidLink } from "react-plaid-link";
import "./FundingSource.scss";
import LoadingButton from "Components/LoadingButton";
import bankIcon from "Assets/Icons/bank.svg";
import useVipApiFetcher, {
  handleError,
  handleResponse,
  handleOkResponse,
} from "Utils/Hooks/useVipApiFetcher";
import { Button } from "react-bootstrap";
import useNative from "Utils/Hooks/useNative";
import useTransactionInitiated from "Utils/Hooks/useTransactionInitiated";
import { Routes, PRODUCTTYPES } from "Utils/Constants";
import useMode, { MODE } from "Utils/Hooks/useMode";
import { useHistory } from "react-router-dom";
import { endingIn } from "Utils/Utils";
import useSession from "Utils/Hooks/useSession";
import { faro } from "@grafana/faro-react";

const Plaid = ({
  onDataChanged,
  onSuccess,
  linkToken,
  enrollmentStep,
  receivedRedirectUri,
  isPlaidRedirect,
  buttonText,
}) => {
  const { session } = useSession();
  const productType =
    session?.patronProductType === PRODUCTTYPES.PREFERRED
      ? "VIP Preferred"
      : "VIP Online";
  const vipApi = useVipApiFetcher();
  const mode = useMode();
  const [isLoading, setIsLoading] = useState(false);
  const isNative = useNative();
  const [submitted, setSubmitted] = useState(false);
  const { transactionInitiated } = useTransactionInitiated();
  const isDepositMode = mode === MODE.deposit;
  const history = useHistory();

  const onSuccessDefault = async (_, metadata) => {
    setIsLoading(true);
    try {
      await vipApi
        .fetch("/api/plaid/account", "PUT", {
          accountId: metadata.account_id,
          accountToken: metadata.public_token,
        })
        .then((res) => {
          res.json().then((data) => {
            onDataChanged({
              session: data,
              metadata: metadata,
            });
            if (!res.ok) {
              faro.api.pushError(new Error("Plaid Update Error"), {
                context: data,
              });
            }
          });
        });
    } catch (error) {
      faro.api.pushError(new Error("Plaid Update Network Error"), {
        context: error,
      });
    }
  };

  const onPlaidSuccess = async (metadata) => {
    if (metadata) {
      try {
        const plaidMetadata = JSON.parse(metadata);
        if (enrollmentStep) {
          onSuccessDefault(null, plaidMetadata);
        } else {
          await onSubmitCommon(
            metadata.account.mask,
            {
              accountId: plaidMetadata.account_id,
              accountToken: plaidMetadata.public_token,
            },
            "/api/bank/plaid"
          );
        }
      } catch (err) {
        const customErrorButtons =
          mode === MODE.manageBanks && transactionInitiated
            ? [
                {
                  label: "TRY AGAIN",
                  route: Routes.MY_ACCOUNTS,
                },
              ]
            : transactionInitiated
              ? [
                  {
                    label: "TRY AGAIN",
                    route: Routes.MY_ACCOUNTS,
                  },
                  {
                    label: `COMPLETE ${isDepositMode ? "DEPOSIT" : "WITHDRAW"}`,
                    route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
                  },
                ]
              : undefined;
        faro.api.pushError(new Error("Plaid Native Link Error"), {
          context: err,
        });
        handleError(err, history, customErrorButtons);
      }
    }
  };

  const onSubmitCommon = async (
    accountNumberDisplay,
    body,
    url = "/api/bank"
  ) => {
    setIsLoading(true);
    setSubmitted(true);
    const customErrorButtons =
      mode === MODE.manageBanks && transactionInitiated
        ? [
            {
              label: "TRY AGAIN",
              route: Routes.MY_ACCOUNTS,
            },
          ]
        : transactionInitiated
          ? [
              {
                label: "TRY AGAIN",
                route: Routes.MY_ACCOUNTS,
              },
              {
                label: `COMPLETE ${isDepositMode ? "DEPOSIT" : "WITHDRAW"}`,
                route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
              },
            ]
          : undefined;
    try {
      vipApi
        .fetch(url, "POST", body)
        .then((response) => handleResponse(history, response))
        .then((response) =>
          handleOkResponse({
            history,
            response,
            title: "Funding Source Added", //`${fundTypeName} Added`,
            message: `You successfully added your ${response.body.bankName?.toUpperCase()} account${endingIn(
              accountNumberDisplay
            )} to your ${productType} account.`,
            errorCustomButtons: customErrorButtons,
          })
        );
    } catch (error) {
      faro.api.pushError(new Error("Plaid Add Bank Network Error"), {
        context: error,
      });
      handleError(error, history, customErrorButtons);
    }
    localStorage.removeItem("link_token");
  };

  const openNativeLink = () => {
    faro.api.pushEvent(
      "User clicked to Add Bank Account using Plaid Native Link"
    );
    window.$onAndroidSuccess = function (metadata) {
      onAndroidSuccess(metadata);
    };

    window.$onAndroidError = function (error) {
      onAndroidError(error);
    };
    let message = {
      linkToken: linkToken,
    };

    var plaidTokenForAndroid = linkToken;
    if (/android/i.test(navigator.userAgent) || window?.Android) {
      handleAndroidMessage(plaidTokenForAndroid);
    }
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) || window?.webkit) {
      var iosPromise =
        window?.webkit?.messageHandlers?.NativeBridge?.postMessage(message);
      iosPromise
        .then(
          function (result) {
            onPlaidSuccess(result);
          },
          function (err) {
            console.log(err);
            faro.api.pushError(new Error("Plaid Native Link iOS Error"), {
              context: err,
            });
          }
        )
        .finally(() => {
          //iosPromise.dispose();
        });
    }
  };

  function handleAndroidMessage(message) {
    return new Promise((resolve, reject) => {
      window?.Android?.openLinkNative(
        message,
        onAndroidSuccess.name,
        onAndroidError.name
      );
    });
  }

  function onAndroidSuccess(metadata) {
    console.log("onAndroidSuccess called");
    console.log(metadata);
    onPlaidSuccess(metadata);
  }

  function onAndroidError(error) {
    console.log("onAndroidError called");
    console.log(error);
    faro.api.pushError(new Error("Plaid Native Link Android Error"), {
      context: error,
    });
  }

  const loadHandler = () => {
    window.sessionStorage.setItem("plaidlaunch", true);
    if (isPlaidRedirect) {
      const open = document.getElementsByClassName("ready")[0];
      open.click();
    }
  };

  const onExit = (error, metadata) => {
    window.sessionStorage.setItem("plaidlaunch", false);
  };

  let button;

  if (isLoading) {
    button = <LoadingButton />;
  } else {
    button = !isNative ? (
      <PlaidLink
        token={linkToken}
        onSuccess={onSuccess ?? onSuccessDefault}
        className="ready click-add-plaid"
        onLoad={loadHandler}
        receivedRedirectUri={receivedRedirectUri}
        onExit={onExit}
      >
        <img className="bank-icon" src={bankIcon} alt="Bank Icon" />
        <div className="bank-label">{buttonText}</div>
      </PlaidLink>
    ) : (
      <Button className="ready click-add-plaid" onClick={openNativeLink}>
        <img className="bank-icon" src={bankIcon} alt="Bank Icon"></img>
        <div className="bank-label">{buttonText}</div>
      </Button>
    );
  }

  return (
    <>
      {isPlaidRedirect && linkToken && (
        <PlaidLink
          token={linkToken}
          onSuccess={onSuccess ?? onSuccessDefault}
          className="ready"
          onLoad={loadHandler}
          receivedRedirectUri={receivedRedirectUri}
          onExit={onExit}
        ></PlaidLink>
      )}
      {!isPlaidRedirect && (
        <div className="funding-source">{linkToken && button}</div>
      )}
    </>
  );
};
export default Plaid;
